// Icônes de marque des serveurs MCP distants (02/10). L'amont n'offre aux serveurs distants qu'un
// jeu d'icônes fixe (CustomResourceIconType) : « Outils Pirouette » et « Service public » s'y
// affichaient avec le ⌘ générique. Le fork leur substitue une image servie par la SPA
// (front/public/static/marques/, publiée avec elle sous sa base, /chat/ en production) :
//   - Outils Pirouette (serveur « astraly-outils-metier ») : la mascotte Pirouette ;
//   - Service public (serveur « service-public », mcp-service-public.*) : l'emblème tricolore
//     de l'ancien connecteur de l'app Pirouette (astraly-fr/app-pirouette,
//     frontend/public/ext-service-public.svg, dessin maison sans licence tierce).
// Tout autre serveur garde l'icône de l'amont (null).

export const IMAGE_OUTILS_PIROUETTE = "static/marques/pirouette/assistant.png";
export const IMAGE_OUTILS_BOOSTS = "static/marques/boosts/assistant.png";

/**
 * Plusieurs apps sur une instance (sous-plan 11, 03/10) : le serveur des outils métier s'appelle
 * « astraly-outils-metier » chez chacune. Son image suit la marque de la SPA (NEXT_PUBLIC_MARQUE,
 * posée par scripts/astraly/apps/<app>.env) ; sans marque, Pirouette, comme avant.
 */
function marqueValide(
  marque: string | undefined = process.env.NEXT_PUBLIC_MARQUE
): string | null {
  return typeof marque === "string" && /^[a-z0-9-]+$/.test(marque)
    ? marque
    : null;
}

function imageOutilsMetier(): string {
  const marque = marqueValide();
  return marque
    ? `static/marques/${marque}/assistant.png`
    : IMAGE_OUTILS_PIROUETTE;
}
export const IMAGE_SERVICE_PUBLIC = "static/marques/service-public/icone.svg";

const PAR_NOM: Record<string, string> = {
  outilspirouette: IMAGE_OUTILS_PIROUETTE,
  outilsboosts: IMAGE_OUTILS_BOOSTS,
  servicepublic: IMAGE_SERVICE_PUBLIC,
  servicepublicfr: IMAGE_SERVICE_PUBLIC,
};

const SEPARATEUR_APPEL = "__";

function normaliser(nom: string | null | undefined): string {
  return (nom ?? "").toLowerCase().replace(/[\s_-]+/g, "");
}

/** Base de la SPA (VITE_BASE_PATH, « /chat/ » en production ; « / » sinon). */
export function baseSpa(): string {
  return import.meta.env?.VITE_BASE_PATH ?? "/";
}

function sousLaBase(chemin: string, base: string): string {
  return `${(base || "/").replace(/\/+$/, "")}/${chemin}`;
}

/**
 * Marques qui ont leur propre mascotte dans le panneau Navigateur (sous-plan 12 ; Boosts depuis
 * le 22/10, demande de Louka : sa planète au lieu de la girafe). Liste fermée : Pirouette et toute
 * autre marque gardent la mascotte Pirouette, comme avant.
 */
const MARQUES_AVEC_MASCOTTE: ReadonlySet<string> = new Set([
  "boosts",
  "electron",
]);

/** Mascotte sous la base de la SPA (écran vide du panneau Navigateur). */
export function imageMascotte(base: string = baseSpa()): string {
  const marque = marqueValide();
  return sousLaBase(
    marque && MARQUES_AVEC_MASCOTTE.has(marque)
      ? `static/marques/${marque}/assistant.png`
      : IMAGE_OUTILS_PIROUETTE,
    base
  );
}

/**
 * Règle générique « outils<app> » (sous-plan 12) : la vue du serveur des outils métier est nommée
 * « outils_<app> » (« Outils Electron » une fois affichée) ; elle prend l'avatar de la marque de
 * la SPA. Limitée à cette marque : aucune image n'est supposée pour une app que la SPA ne sert pas.
 */
function estOutilsDeLaMarque(cle: string): boolean {
  const marque = marqueValide();
  return marque !== null && cle === `outils${normaliser(marque)}`;
}

function cheminParNom(nom: string | null | undefined): string | null {
  const cle = normaliser(nom);
  return cle === "astralyoutilsmetier" || estOutilsDeLaMarque(cle)
    ? imageOutilsMetier()
    : (PAR_NOM[cle] ?? null);
}

function cheminParAdresse(url: string | null | undefined): string | null {
  if (typeof url !== "string" || !url) {
    return null;
  }
  try {
    return new URL(url).hostname.startsWith("mcp-service-public.")
      ? IMAGE_SERVICE_PUBLIC
      : null;
  } catch {
    return null;
  }
}

export type ServeurPourIcone = {
  name?: string | null;
  url?: string | null;
};

/**
 * Image de marque d'un serveur MCP (nom du serveur, adresse distante, ou nom affiché de sa vue),
 * sous la base de la SPA ; null si le serveur garde l'icône de l'amont.
 */
export function imageOutil(
  serveur: ServeurPourIcone,
  base: string = baseSpa(),
  nomDeVue?: string | null
): string | null {
  const chemin =
    cheminParNom(serveur.name) ??
    cheminParNom(nomDeVue) ??
    cheminParAdresse(serveur.url);
  return chemin ? sousLaBase(chemin, base) : null;
}

/** Même chose depuis le seul nom de serveur (métadonnées d'une demande de validation). */
export function imageOutilDepuisNomServeur(
  nom: string | null | undefined,
  base: string = baseSpa()
): string | null {
  const chemin = cheminParNom(nom);
  return chemin ? sousLaBase(chemin, base) : null;
}

/**
 * Même chose depuis un nom d'appel d'outil (« outils_pirouette__browser_open ») : le serveur est
 * le segment qui précède le nom de l'outil (le dernier d'un serveur imbriqué).
 */
export function imageOutilDepuisNomDAppel(
  nomDAppel: string | null | undefined,
  base: string = baseSpa()
): string | null {
  if (!nomDAppel) {
    return null;
  }
  const segments = nomDAppel.split(SEPARATEUR_APPEL);
  if (segments.length < 2) {
    return null;
  }
  return imageOutilDepuisNomServeur(segments[segments.length - 2], base);
}
